const TOKEN_KEY = 'periscope.admin.token';

export interface StoredToken {
  accessToken: string;
  /** ISO-8601 timestamp. */
  expiresAt: string;
}

/**
 * The access token is kept in `localStorage` so a page refresh does not force a
 * re-login. That is readable by any script on the origin, which is an accepted
 * trade for a single-account internal tool with short-lived tokens — it is the
 * reason the app ships no third-party scripts and no user-authored HTML.
 *
 * Access is funnelled through this module so the storage choice can be changed
 * (in-memory, or an httpOnly cookie plus a refresh endpoint) in one place.
 */
export const tokenStorage = {
  read(): StoredToken | null {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);

      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Partial<StoredToken>;

      if (typeof parsed.accessToken !== 'string' || typeof parsed.expiresAt !== 'string') {
        return null;
      }

      // Drop it rather than sending a token the server is certain to reject.
      if (isExpired(parsed.expiresAt)) {
        localStorage.removeItem(TOKEN_KEY);
        return null;
      }

      return { accessToken: parsed.accessToken, expiresAt: parsed.expiresAt };
    } catch {
      // Corrupt JSON, or storage blocked entirely (private mode, strict policy).
      return null;
    }
  },

  write(token: StoredToken): void {
    try {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    } catch {
      // Session simply will not survive a refresh; not worth breaking login over.
    }
  },

  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing useful to do */
    }
  },
};

export function isExpired(expiresAt: string, skewMs = 30_000): boolean {
  const expiry = Date.parse(expiresAt);

  if (Number.isNaN(expiry)) {
    return true;
  }

  // Treat a token that is about to expire as already expired, so a request in
  // flight cannot land after the deadline.
  return expiry - skewMs <= Date.now();
}
